<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const errors = ref([]);
const isLoading = ref(false);
let requestId = 0;

const errorTypeLabels = {
  invalid_phone: t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TYPES.INVALID_PHONE'),
  template_error: t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TYPES.TEMPLATE_ERROR'),
  rate_limit: t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TYPES.RATE_LIMIT'),
  api_error: t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TYPES.API_ERROR'),
  unknown: t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TYPES.UNKNOWN'),
};

const campaignTitle = computed(() => props.campaign?.title || '');

const fetchErrors = async () => {
  if (!props.campaign?.id) return;

  requestId += 1;
  const currentRequest = requestId;
  errors.value = [];
  isLoading.value = true;
  try {
    const response = await CampaignsAPI.getDeliveryErrors(props.campaign.id);
    if (currentRequest === requestId) errors.value = response.data.payload;
  } catch {
    if (currentRequest === requestId) errors.value = [];
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const open = () => {
  fetchErrors();
  dialogRef.value.open();
};

const onClose = () => {
  requestId += 1;
  errors.value = [];
};

defineExpose({ dialogRef: { open } });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TITLE')"
    width="3xl"
    overflow-y-auto
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.CLOSE')"
    @close="onClose"
  >
    <div class="flex flex-col gap-4">
      <div class="text-sm text-n-slate-11">
        {{
          t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.SUBTITLE', {
            campaign: campaignTitle,
          })
        }}
      </div>

      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <Spinner />
      </div>

      <div
        v-else-if="errors.length === 0"
        class="py-8 text-center text-n-slate-11"
      >
        {{ t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.NO_ERRORS') }}
      </div>

      <div v-else class="flex flex-col gap-2 max-h-96 overflow-y-auto">
        <div
          v-for="error in errors"
          :key="error.id"
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2 border border-n-weak"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <span class="i-lucide-user size-4 text-n-slate-11 shrink-0" />
              <span class="font-medium text-sm text-n-slate-12 truncate">
                {{
                  error.contact?.name ||
                  t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.UNKNOWN_CONTACT')
                }}
              </span>
            </div>
            <span
              class="text-xs px-2 py-0.5 rounded-md bg-n-ruby-3 text-n-ruby-11 shrink-0"
            >
              {{ errorTypeLabels[error.error_type] || error.error_type }}
            </span>
          </div>

          <div class="flex items-center gap-2 text-xs text-n-slate-11">
            <span class="i-lucide-phone size-3.5" />
            <span>{{ error.phone_number || '-' }}</span>
          </div>

          <div
            v-if="error.label_title"
            class="flex items-center gap-2 text-xs text-n-slate-11"
          >
            <span class="i-lucide-tag size-3.5" />
            <span>{{ error.label_title }}</span>
          </div>

          <div
            v-if="error.error_message"
            class="mt-1 text-xs text-n-ruby-11 bg-n-ruby-2 p-2 rounded"
          >
            {{ error.error_message }}
          </div>
        </div>
      </div>

      <div v-if="errors.length > 0" class="text-xs text-n-slate-11 text-center">
        {{
          t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TOTAL', { count: errors.length })
        }}
      </div>
    </div>
  </Dialog>
</template>
